import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faTerminal,
  faClock,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PublicFooter from '../../components/layout/PublicFooter';
import { learningPaths, popularTopicGroups, learnByDoing, isArticlePublished } from './content';
import { publicArticlesApi } from '../../services/api/articlesApi';
import { usePageMeta } from '../../hooks/usePageMeta';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  const style: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(18px)',
    transition: 'opacity 0.7s ease, transform 0.7s ease',
  };
  return { ref, style };
}

const difficultyLabel: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

const levelColor: Record<string, string> = {
  'Beginner Path': 'text-emerald-400 border-emerald-400/30',
  'Intermediate Path': 'text-blue-400 border-blue-400/30',
  'Advanced Path': 'text-amber-400 border-amber-400/30',
};

export default function LearnHubPage() {
  usePageMeta({ title: 'Learn Systematic Trading', description: 'Structured learning paths from backtesting basics to Walk-Forward Analysis. Learn how to build, test, and validate trading strategies properly.' });
  const revHero = useScrollReveal(0.1);
  const revTopics = useScrollReveal(0.1);
  const revPaths = useScrollReveal(0.1);
  const revDoing = useScrollReveal(0.1);
  const revTrust = useScrollReveal(0.1);
  const revCta = useScrollReveal(0.1);

  // Fetch published slugs from API to know which articles are live
  const [publishedSlugs, setPublishedSlugs] = useState<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    publicArticlesApi.list()
      .then((res) => {
        if (!cancelled && res.success) {
          setPublishedSlugs(new Set(res.articles.map((a) => a.slug)));
        }
      })
      .catch(() => { /* fall through to static check */ });
    return () => { cancelled = true; };
  }, []);

  const isPublished = (slug: string) => publishedSlugs.has(slug) || isArticlePublished(slug);

  return (
    <div className="min-h-screen bg-[#0a0e14] text-white selection:bg-[#9d4edd]/30 selection:text-white">
      <PublicNavBar />

      {/* Hero */}
      <section
        ref={revHero.ref as React.RefObject<HTMLElement>}
        style={revHero.style}
        className="pt-32 pb-16 px-4 sm:px-6 max-w-4xl mx-auto text-center"
      >
        <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/60 block mb-4">
          Learn Systematic Trading
        </span>
        <h1 className="text-[28px] sm:text-[36px] font-semibold leading-tight tracking-tight mb-6">
          Build, backtest, and validate trading strategies — properly
        </h1>
        <p className="text-[15px] text-gray-400 leading-relaxed max-w-2xl mx-auto">
          Structured learning paths from backtesting basics to Walk-Forward
          Analysis and live deployment. Learn why most strategies fail, how to
          avoid overfitting, and how to prove your edge is real.
        </p>
      </section>

      {/* Popular Topics — grouped (SEO entry layer) */}
      <section
        ref={revTopics.ref as React.RefObject<HTMLElement>}
        style={revTopics.style}
        className="pb-20 px-4 sm:px-6 max-w-4xl mx-auto"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {popularTopicGroups.map((group) => (
            <div key={group.label}>
              <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-gray-500 block mb-3">
                {group.label}
              </span>
              <div className="space-y-2">
                {group.topics.map((topic) => {
                  const published = isPublished(topic.slug);
                  return published ? (
                    <Link
                      key={topic.slug}
                      to={`/learn/${topic.category}/${topic.slug}`}
                      className="block text-[13px] text-gray-300 border border-gray-800 rounded-lg px-3.5 py-2.5 hover:border-gray-600 hover:text-white transition-colors bg-gray-900/20"
                    >
                      {topic.title}
                    </Link>
                  ) : (
                    <div
                      key={topic.slug}
                      className="flex items-center justify-between text-[13px] text-gray-500 border border-gray-800/50 rounded-lg px-3.5 py-2.5 bg-gray-900/10 cursor-default"
                    >
                      <span>{topic.title}</span>
                      <span className="text-[9px] font-mono tracking-wider uppercase text-gray-600 border border-gray-700/50 rounded-full px-2 py-0.5">Soon</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Learning Paths */}
      <section
        ref={revPaths.ref as React.RefObject<HTMLElement>}
        style={revPaths.style}
        className="pb-20 px-4 sm:px-6 max-w-6xl mx-auto"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {learningPaths.map((path) => (
            <Link
              key={path.id}
              to={`/learn/${path.slug}`}
              className="group border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-colors bg-gray-900/30"
            >
              {/* Path header */}
              <div className="flex items-start gap-4 mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${path.color}15` }}
                >
                  <FontAwesomeIcon
                    icon={path.icon}
                    className="text-sm"
                    style={{ color: path.color }}
                  />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-[10px] font-mono border rounded-full px-2 py-0.5 ${levelColor[path.level]}`}
                    >
                      {path.level}
                    </span>
                    <span className="text-[10px] font-mono text-gray-600 flex items-center gap-1">
                      <FontAwesomeIcon icon={faClock} className="text-[9px]" />
                      {path.estimatedTime}
                    </span>
                  </div>
                  <h2 className="text-[17px] font-semibold text-white group-hover:text-[#9d4edd] transition-colors">
                    {path.title}
                  </h2>
                  <span className="text-[12px] text-gray-500 italic">
                    {path.tagline}
                  </span>
                </div>
              </div>

              <p className="text-[13px] text-gray-400 leading-relaxed mb-5">
                {path.description}
              </p>

              {/* Article list */}
              <div className="space-y-2.5 mb-5">
                {path.articles.map((article, i) => {
                  const pub = isPublished(article.slug);
                  return (
                    <div
                      key={article.slug}
                      className="flex items-center gap-3 text-[13px]"
                    >
                      <span className="w-5 h-5 rounded-full border border-gray-700 flex items-center justify-center text-[10px] text-gray-500 shrink-0">
                        {i + 1}
                      </span>
                      <span className={pub ? 'text-gray-300' : 'text-gray-500'}>{article.title}</span>
                      <span className="ml-auto text-[10px] font-mono text-gray-600">
                        {pub ? difficultyLabel[article.difficulty] : 'Soon'}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 text-[13px] font-medium text-[#9d4edd] group-hover:gap-3 transition-all">
                Start path
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Learn by Doing — journey */}
      <section
        ref={revDoing.ref as React.RefObject<HTMLElement>}
        style={revDoing.style}
        className="pb-20 px-4 sm:px-6 max-w-4xl mx-auto border-t border-gray-800/40 pt-16"
      >
        <div className="flex items-center gap-2.5 mb-2">
          <FontAwesomeIcon
            icon={faTerminal}
            className="text-xs text-[#9d4edd]/60"
          />
          <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/60">
            Learn by Doing
          </span>
        </div>
        <h2 className="text-[20px] font-semibold mb-2">
          Follow a real example from start to finish
        </h2>
        <p className="text-[14px] text-gray-400 mb-8 max-w-2xl leading-relaxed">
          Build a strategy, test it against historical data, and validate it
          with professional-grade tools.
        </p>

        {/* Journey steps with connector line */}
        <div className="relative pl-5">
          {/* Vertical connector */}
          <div className="absolute left-[11px] top-3 bottom-3 w-px bg-gradient-to-b from-[#9d4edd]/40 via-[#3b82f6]/30 to-[#10b981]/40" />

          <div className="space-y-1">
            {learnByDoing.map((item, i) => {
              const published = isPublished(item.slug);
              const Wrapper = published ? Link : 'div' as const;
              const wrapperProps = published
                ? { to: `/learn/${item.category}/${item.slug}` }
                : {};
              return (
                <Wrapper
                  key={item.slug}
                  {...(wrapperProps as Record<string, string>)}
                  className={`group relative flex items-start gap-4 rounded-lg px-4 py-3.5 transition-colors ${published ? 'hover:bg-gray-900/40 cursor-pointer' : 'cursor-default opacity-60'}`}
                >
                  {/* Step dot */}
                  <span className={`relative z-10 w-[22px] h-[22px] rounded-full border-2 border-gray-700 bg-[#0a0e14] flex items-center justify-center text-[10px] font-mono text-gray-500 shrink-0 transition-colors -ml-4 ${published ? 'group-hover:border-[#9d4edd] group-hover:text-[#9d4edd]' : ''}`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-mono tracking-[0.1em] uppercase text-gray-600 block mb-0.5">
                      Step {i + 1} — {item.step}
                    </span>
                    <span className={`text-[14px] transition-colors ${published ? 'text-gray-300 group-hover:text-white' : 'text-gray-500'}`}>
                      {item.title}
                    </span>
                  </div>
                  {published ? (
                    <FontAwesomeIcon
                      icon={faArrowRight}
                      className="mt-3 text-xs text-gray-700 group-hover:text-[#9d4edd] transition-colors"
                    />
                  ) : (
                    <span className="mt-3 text-[9px] font-mono tracking-wider uppercase text-gray-600">Soon</span>
                  )}
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust signal */}
      <section
        ref={revTrust.ref as React.RefObject<HTMLElement>}
        style={revTrust.style}
        className="pb-12 px-4 sm:px-6 max-w-3xl mx-auto text-center"
      >
        <div className="inline-flex items-center gap-2 text-[12px] text-gray-500 font-mono border border-gray-800/60 rounded-full px-4 py-2">
          <FontAwesomeIcon
            icon={faShieldHalved}
            className="text-[11px] text-[#9d4edd]/50"
          />
          Designed to avoid overfitting — built for real strategy validation
        </div>
      </section>

      {/* Bottom CTA */}
      <section
        ref={revCta.ref as React.RefObject<HTMLElement>}
        style={revCta.style}
        className="pb-24 px-4 sm:px-6 max-w-3xl mx-auto text-center border-t border-gray-800/40 pt-16"
      >
        <h2 className="text-[20px] font-semibold mb-3">
          Stop guessing. Start validating your strategies with real data.
        </h2>
        <p className="text-[14px] text-gray-400 mb-6 max-w-xl mx-auto leading-relaxed">
          Quanthop gives you the backtesting engine, strategy language, and
          validation pipeline to build trading systems that actually hold up in
          live markets.
        </p>
        <Link
          to="/signup"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#9d4edd] hover:bg-[#8b3ec7] text-white text-[14px] font-medium transition-colors"
        >
          Build, test, and validate your first strategy — properly
          <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
        </Link>
      </section>

      <PublicFooter />
    </div>
  );
}
